import React from "react";
import { Navigate } from "react-router-dom";
import CircularProgress from "@mui/material/CircularProgress";
import styled from "styled-components";

import { useFleetAuth } from "../../auth/fleetAuth";

/**
 * Where the Cognito hosted UI returns after sign-in.
 *
 * This route has to exist, and it has to hand control back through the ROUTER.
 * The provider's `onSigninCallback` cannot do it: `history.replaceState` changes
 * the address bar without telling React Router, so the router would still
 * believe it is on /callback, match nothing, and render an empty page while the
 * URL read "/" -- a blank screen after a successful login, which is the least
 * debuggable failure a sign-in can have.
 *
 * `<Navigate>` is a router-level transition, so it both leaves this route and
 * drops `?code=&state=` from the URL.
 *
 * Under Auth0 nothing reaches here: its `redirect_uri` is the origin root. The
 * route is harmless in that build and keeps the two providers' paths from being
 * conditional on each other.
 */
const AuthCallback = () => {
  const { isLoading } = useFleetAuth();

  // Hold while the authorization code is exchanged. Leaving early would land on
  // "/" with no session and bounce straight back into a second login.
  if (isLoading) {
    return (
      <Centered>
        <CircularProgress size={40} />
        <Text>Signing you in…</Text>
      </Centered>
    );
  }

  // Unconditionally home, whether the exchange succeeded or not. On success the
  // session is established; on failure the provider's `error` travels with the
  // session state, and RequireAuth on "/" shows it with a Try Again instead of
  // starting another login — so a persistent callback failure is a message,
  // not a loop, and this route needs no error handling of its own.
  return <Navigate to="/" replace />;
};

const Centered = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100vh;
  gap: 16px;
`;

const Text = styled.p`
  color: ${(props) => props.theme.colors.grey};
  font-size: 14px;
`;

export default AuthCallback;
